import { CompleteFormNameMapping, VisaPurposes } from 'constant';
import { getVisaApplicationPath } from 'utils/v2/visaApplication';

/**
 * The GENERAL path contains every section, so it fixes the order candidates see
 * in the client-view assessment. Deriving it here keeps the two in step.
 */
const clientViewSectionOrder: string[] = getVisaApplicationPath(
    VisaPurposes.GENERAL,
    'Yes',
    'Yes'
).flatMap((step) => CompleteFormNameMapping[step] ?? []);

/**
 * Orders sections as the candidate meets them; unrecognised ones keep API order at the end.
 * The API only emits sections with measured time: `purpose` is folded into `personal_info`,
 * a fallback-anchored first section is omitted, and the `assessment_start` /
 * `assessment_complete` markers are never stages.
 */
export const compareAssessmentSections = (a: string, b: string): number => {
    const rankOf = (section: string) => {
        const index = clientViewSectionOrder.indexOf(section);
        return index === -1 ? clientViewSectionOrder.length : index;
    };
    return rankOf(a) - rankOf(b);
};

const stageLabelKeys: Record<string, string> = {
    personal_info: 'assessmentStagePersonalInfo',
    additional_details: 'assessmentStageAdditionalDetails',
    work_experience: 'assessmentStageWorkExperience',
    education: 'assessmentStageEducation',
    language: 'assessmentStageLanguage',
    relative_abroad: 'assessmentStageRelativeAbroad',
    job_offer: 'assessmentStageJobOffer',
    business: 'assessmentStageBusiness',
    spouse: 'assessmentStageSpouse',
    family_member: 'assessmentStageFamilyMember',
};

const humanizeIdentifier = (value: string): string => {
    const words = value
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/[_-]+/g, ' ')
        .trim()
        .toLowerCase();

    return words ? `${words.charAt(0).toUpperCase()}${words.slice(1)}` : value;
};

export const getAssessmentStageLabel = (
    stage: string,
    translate: (key: string) => string
): string => {
    const key = stageLabelKeys[stage];
    return key ? translate(key) : humanizeIdentifier(stage);
};
